import { Transition } from "@headlessui/react";
import { ClipboardCheckIcon, ClipboardCopyIcon, DocumentTextIcon, FilterIcon, PencilIcon, RefreshIcon, XIcon } from "@heroicons/react/outline";
import { useContext, useState } from "react";
import { createPortal } from "react-dom";
import CopyToClipboard from "react-copy-to-clipboard";
import { putData } from "../backend";
import { useModal, WorkflowContext } from "../contexts";
import { BLOCK_ICON_MAP } from "../utils";
import WorkflowSelectModal from "./WorkflowSelectModal";
import WebhookEventTableRow from "./WebhookEventTableRow";
import WorkflowModalWebhookNameChange from "./WorkflowModalWebhookNameChange";
import WorkflowSourceConditionModal from "./WorkflowSourceConditionModal";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";
import { useHistory, useRouteMatch } from "react-router-dom";
import useUser from "../hooks/useUser";
import { useTranslation } from "react-i18next";


function WorkflowSourceTally() {
  const { t } = useTranslation();
  const { openModal } = useModal();

  const { hasPermission } = useUser();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const history = useHistory();
  const { url } = useRouteMatch();

  const source = workflow.source;
  const events = source?.webhookConfig?.events || [];

  const { sourceSettingsPortalNode: portalTarget } = useContext(WorkflowContext);

  const [isCopied, setIsCopied] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasRefreshed, setHasRefreshed] = useState(false);

  if (!source || !source.webhookConfig || source.sourceType !== "TALLY")
    return null;

  const showSourceOptions = workflow.documents.length > 0 || workflow.emails.length > 0;
  const webhookUrl = `${process.env.REACT_APP_WEBHOOK_URL!}/${workflow.source!.webhookConfig!.token}`;

  function handleCopyWebhookUrl() {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1000);
  }

  function handleRefresh() {
    setIsRefreshing(true);
    mutateWorkflow()
      .catch(console.error)
      .finally(() => { setIsRefreshing(false); setHasRefreshed(true) });
  }

  function handleOpenNameModal() {
    openModal(<WorkflowModalWebhookNameChange source={source!} updateBlock={updateWebhookName} />)
  }

  function updateWebhookName(name: string) {
    return putData(`/workflows/sources/${source!.id}/webhook-config/`, { name })
      .then(() => mutateWorkflow())
      .catch(console.error);
  }

  return (
    <>
      {portalTarget && hasPermission(workflow.team, "workflows.edit") &&
        createPortal(
          <>
            {showSourceOptions && <>
              <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} showRefreshSpinning={isRefreshing} disableCopy />

              <div className="flex flex-col gap-1">
                <div className="font-gilroy font-semibold text-gray-600 text-sm">
                  {t("Webhook Address")}
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" className="w-full text-sm bg-gray-100 text-gray-600 border rounded-sm outline-none h-8 px-2" value={webhookUrl} onClick={e => e.currentTarget.select()} readOnly />
                  <CopyToClipboard text={webhookUrl} onCopy={handleCopyWebhookUrl}>
                    <button className="relative flex items-center justify-center border bg-white h-8 w-8 flex-shrink-0 rounded-sm group shadow-sm">
                      <ClipboardCopyIcon className="h-4 w-4 group-hover:text-blue" />
                      <div className="hidden group-hover:block absolute bg-black text-white whitespace-nowrap text-xs top-9 right-4 py-1 px-2 rounded-md rounded-tr-none">
                        {isCopied ? t("Copied") : t("Copy to clipboard")}
                      </div>
                    </button>
                  </CopyToClipboard>
                </div>
                <div className="text-gray-600 text-xs">
                  For help please checkout our <a href="https://www.portant.co/docs/sources/webhooks/" target="_blank" rel="noreferrer" className="text-blue underline">guide</a> on webhooks
                </div>
              </div>

              <div className="flex flex-col">
                <div className="flex items-center font-gilroy font-semibold text-gray-600">
                  <FilterIcon className="w-4 h-4 mr-2" /> {t("Source Filter Conditions")}
                </div>
                <div className="flex items-center font-gilroy font-semibold text-sm text-gray-400">
                  {t("Only process data that meets certain conditions")}
                </div>
                <button className="btn btn-white text-sm w-52 mt-2" onClick={() => openModal(<WorkflowSourceConditionModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                  {source!.sourceConditions.length === 0 ? t("Add Conditions") : t("Edit Conditions")}
                </button>
              </div>
            </>}

            {!showSourceOptions && <>
              <div className="flex flex-col gap-4 justify-center">
                <div className="flex items-center">
                  <DocumentTextIcon className="w-6 h-6 mr-2" />
                  <div className="font-gilroy font-semibold text-lg">
                    {t("Connect a Template")}
                  </div>
                </div>
                <div className="flex flex-col gap-2 font-semibold text-gray-600">
                  {t("Create documents and presentations from a template")}
                </div>
                <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Select Template")} subtitle={t("Create or select a template to merge your data with")} showDocuments workflowContext={workflowContext} />)}>
                  {t("Connect")}
                </button>
              </div>
            </>}
          </>,
          portalTarget
        )
      }

      <Transition.Child className="flex flex-col bg-white w-full m-4 p-4 pt-2 shadow rounded"
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex w-full items-center mb-2">
          <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
            <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP[source.sourceType]} alt="Tally Icon" />
          </div>
          <div className="flex flex-col h-full justify-center">
            <div className="flex text-sm font-semibold font-gilroy">
              <div className="truncate max-w-[400px]">
                {source.webhookConfig?.name || "Untitled Tally Source"}
              </div>
            </div>
            {hasPermission(workflow.team, "workflows.edit") &&
              <button className="flex items-center text-gray-500 text-sm font-gilroy font-medium hover:underline hover:text-gray-600 cursor-pointer" onClick={handleOpenNameModal}>
                <PencilIcon className="h-4 mr-1" /> {t("Rename")}
              </button>
            }
          </div>
          <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        </div>

        {events.length > 0 &&
          <div className="w-full h-full border border-gray-400 rounded py-2 overflow-hidden">
            <div className="relative flex items-center border-b px-4 text-sm h-[40px]">
              <div className="w-1/6">{t("Origin")}</div>
              <div className="w-1/2">{t("Event ID")}</div>
              <div className="w-1/6">{t("Status")}</div>
              <div className="w-1/6">{t("Received")}</div>
              <RefreshIcon className={`absolute right-4 top-2 w-5 h-5 cursor-pointer text-gray-600 hover:text-blue ${isRefreshing ? "animate-spin" : "animate-none"}`} onClick={handleRefresh} />
            </div>
            <div className="overflow-y-scroll" style={{ height: "calc(100% - 40px)" }}>
              {events.map((event) => <WebhookEventTableRow key={event.id} event={event} />)}
            </div>
          </div>
        }

        {events.length === 0 &&
          <div className="flex flex-col ml-10 mt-8 overflow-y-auto gap-8">

            <div className="flex flex-col gap-4">

              <div className="font-semibold font-gilroy text-gray-600">
                {t("How to use a Tally source with Portant")}
              </div>

              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center font-semibold text-sm rounded-full w-8 h-8 mr-2 bg-gray-400 text-white pt-1">
                  1
                </div>
                <span>
                  {t("Open your Tally form and go the the")} <span className="font-semibold">{t("Integrations")}</span> → <span className="font-semibold">{t("Webhooks")}</span> {t("tab")}
                </span>
              </div>

              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center font-semibold text-sm rounded-full w-8 h-8 mr-2 bg-gray-400 text-white pt-1">
                  2
                </div>
                <span>
                  {t("Select")} <span className="font-semibold">{t("Connect a Webhook")}</span> {t("then copy and paste the")} <span className="font-semibold">{t("Webhook Address")}</span> {t("below into Tally")}
                </span>
              </div>

              <div className="flex flex-col items-start gap-2 ml-10 -mt-2">
                <input type="text" className="w-[520px] text-sm bg-gray-100 text-gray-600 border rounded-sm outline-none h-8 px-2 flex-shrink-0" value={webhookUrl} onClick={e => e.currentTarget.select()} readOnly />
                <CopyToClipboard text={webhookUrl} onCopy={handleCopyWebhookUrl}>
                  {isCopied
                    ? <button className="btn btn-green text-sm w-40 h-8 py-0"><ClipboardCheckIcon className="w-4 h-4 mr-2" />{t("Copied")}</button>
                    : <button className="btn btn-white text-sm w-40 h-8 py-0"><ClipboardCopyIcon className="w-4 h-4 mr-2" />{t("Copy")}</button>
                  }
                </CopyToClipboard>
              </div>

              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center font-semibold text-sm rounded-full w-8 h-8 mr-2 bg-gray-400 text-white pt-1">
                  3
                </div>
                <span>
                  {t("In Tally, open the webhook")} <span className="font-semibold">{t("events log (🕓)")}</span> {t("and send a test event")}
                </span>
              </div>

              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center font-semibold text-sm rounded-full w-8 h-8 mr-2 bg-gray-400 text-white pt-1">
                  4
                </div>
                <span>
                  <span>{t("Check for new events received in Portant by using the refresh button below")}</span>
                </span>
              </div>

              <div className="flex flex-col items-start gap-2 ml-10 -mt-2">
                {isRefreshing
                  ? <button className="btn btn-white text-sm w-40 h-8 py-0" disabled><RefreshIcon className="w-4 h-4 mr-2 animate-spin" />{t("Refreshing")}</button>
                  : <button className="btn btn-white text-sm w-40 h-8 py-0" onClick={handleRefresh}><RefreshIcon className="w-4 h-4 mr-2" />{t("Refresh")}</button>
                }
                {hasRefreshed &&
                  <div className="flex items-center text-sm font-semibold text-red gap-1">
                    {t("No events have been received yet, please ensure the provided Webhook Address")} <div className="flex items-center justify-center font-semibold text-xs rounded-full w-4 h-4 bg-gray-400 text-white pt-1 mb-1">2</div> {t("is correct in Tally")}
                  </div>
                }
              </div>

              <div className="flex items-center text-sm font-semibold">
                <div className="flex items-center justify-center font-semibold text-2xl mr-2">
                  🎉
                </div>
                <span>
                  <span>{t("You can now add a document template and customise it using the field names from Tally")}</span>
                </span>
              </div>

              <div className="flex items-center text-sm">
                <span>
                  <a href="https://www.portant.co/docs/sources/webhooks/" target="_blank" rel="noreferrer" className="text-blue underline font-semibold cursor-pointer hover:underline">{t("View Complete Webhooks Guide")}</a>
                </span>
              </div>
            </div>

          </div>
        }

      </Transition.Child>
    </>
  );

}

export default WorkflowSourceTally;
